/**
 * Staff-parity order journey rows (matches fasto_staff `getEvents` / timeline).
 * Used from CRM ticket payloads without calling the staff order-detail API.
 */

export type StaffOrderTimelineInput = {
  created_timestamp: string | null;
  packed_timestamp: string | null;
  billed_timestamp: string | null;
  picked_timestamp: string | null;
  reached_timestamp: string | null;
  completed_timestamp: string | null;
  return_timestamp: string | null;
  cancelled_timestamp: string | null;
  cancellation_reason: string | null;
  payment_type: string | null;
  is_payment_done: boolean;
};

export type StaffTimelineRow = {
  id: string;
  stamp: string | null;
  title: string;
  message: string;
  variant: 'pending' | 'done' | 'cancelled';
};

function parseMs(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : null;
}

function diffSec(a: string | null, b: string | null): number | null {
  const ma = parseMs(a);
  const mb = parseMs(b);
  if (ma == null || mb == null) return null;
  return Math.round((mb - ma) / 1000);
}

/** Same shape as `fmt.duration` (seconds). */
export function staffFormatDuration(seconds: number | null | undefined): string {
  if (seconds == null || Number.isNaN(seconds)) return '—';
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

export function staffPaymentTypeLabel(raw: string | null | undefined): string {
  if (!raw) return '—';
  return raw
    .replace(/_/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function titleWithDuration(base: string, seconds: number | null): string {
  if (seconds == null) return base;
  return `${base} (${staffFormatDuration(seconds)})`;
}

type Internal = {
  id: string;
  stamp: string | null;
  titleBase: string;
  durationSec: number | null;
  message: string;
};

/** Build rows after cancellation filter and append cancelled row with reason. */
export function buildStaffOrderTimelineRows(o: StaffOrderTimelineInput): StaffTimelineRow[] {
  const created = o.created_timestamp;
  const packed = o.packed_timestamp;
  const billed = o.billed_timestamp;
  const picked = o.picked_timestamp;
  const reached = o.reached_timestamp;
  const completed = o.completed_timestamp;
  const returned = o.return_timestamp;
  const cancelledAt = o.cancelled_timestamp;

  const cancelMsg = o.cancellation_reason?.trim() || 'Order has been cancelled.';

  const main: Internal[] = [
    {
      id: 'packed',
      stamp: packed,
      titleBase: 'Packed',
      durationSec: diffSec(created, packed),
      message: 'All items packed.',
    },
    {
      id: 'billing',
      stamp: billed,
      titleBase: 'Billing',
      durationSec: packed != null ? diffSec(packed, billed) : null,
      message: 'Billing and invoice done.',
    },
    {
      id: 'picked',
      stamp: picked,
      titleBase: 'Picked',
      durationSec: billed != null ? diffSec(billed, picked) : null,
      message: 'Order picked up by rider.',
    },
    {
      id: 'reached',
      stamp: reached,
      titleBase: 'Reached',
      durationSec: picked != null ? diffSec(picked, reached) : null,
      message: 'Order has reached.',
    },
    {
      id: 'delivered',
      stamp: completed,
      titleBase: 'Delivered',
      durationSec: diffSec(created, completed),
      message: 'Delivery confirmed & completed.',
    },
    {
      id: 'returned',
      stamp: returned,
      titleBase: 'Returned',
      durationSec:
        returned != null && completed != null ? diffSec(completed, returned) : null,
      message: 'Rider back to store.',
    },
    {
      id: 'payment',
      stamp: o.is_payment_done ? returned : null,
      titleBase: 'Completed',
      durationSec: null,
      message: `Payment received (${staffPaymentTypeLabel(o.payment_type)}).`,
    },
  ];

  const cancelMs = parseMs(cancelledAt);
  const mainFiltered =
    cancelMs == null
      ? main
      : main.filter((ev) => {
          const sm = parseMs(ev.stamp);
          if (sm == null) return false;
          return sm <= cancelMs;
        });

  const createdRow: StaffTimelineRow = {
    id: 'created',
    stamp: created,
    title: 'Created',
    message: 'Order logged in the system.',
    variant: created ? 'done' : 'pending',
  };

  const middle = mainFiltered.map((ev): StaffTimelineRow => ({
    id: ev.id,
    stamp: ev.stamp,
    title: titleWithDuration(ev.titleBase, ev.durationSec),
    message: ev.message,
    variant: ev.stamp ? 'done' : 'pending',
  }));

  const out: StaffTimelineRow[] = [createdRow, ...middle];

  if (cancelMs != null && cancelledAt) {
    out.push({
      id: 'cancelled',
      stamp: cancelledAt,
      title: 'Cancelled',
      message: cancelMsg,
      variant: 'cancelled',
    });
  }

  return out;
}

/** Header trailing text: "Reached in Xm Ys" (staff `OrderDetailPage` timeline). */
export function staffReachedInLabel(o: StaffOrderTimelineInput): string | null {
  if (o.cancelled_timestamp) return null;
  const sec = diffSec(o.created_timestamp, o.reached_timestamp);
  if (sec == null) return null;
  return `Reached in ${staffFormatDuration(sec)}`;
}
